import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { Icon, ICON_SIZE } from '../chrome/icons';
import { Spinner } from '../../ui/controls';
import { Popover, useAnchor } from '../../ui/popover';
import { tip } from '../../ui/tooltip';
import { usableBrands, type BrandCard, type BrandKit, type KitPicture } from '../../services/work';
import { useKitFonts, useWork } from '../work';
import { brandRows, essentials, isBare, logoGroups, railMark, type BrandKind } from './brandModel';
import { useFittedLogo } from './fit';
import { addKitText, aimed, Body, KitStyles, kitTextLook, Quiet, tileUrl, Wall, type LibraryActions } from './kinds';
import { carry } from '../carry';
import { PanelHeader } from './parts';
import { svgText } from './svgText';
import { brandChoices } from './brandChoice';

/** The file's brand, a visual brand switcher, and its assets to place on the canvas. */

/** How much of the page's shorter side a brand's drawing lands across. */
const LOGO_SHARE = 0.4;
const ICON_SHARE = 0.16;
/** Paper: what a logo with no ground of its own is shown on. */
const PAPER = '#f2eee3';


/** A picture's own size, from the small rendition a tile already has: the tile itself may show it trimmed. */
function sizeOf(url: string): Promise<{ width: number; height: number }> {
  return new Promise((resolve) => {
    const image = new Image();
    image.onload = () => resolve({ width: image.naturalWidth, height: image.naturalHeight });
    image.onerror = () => resolve({ width: 0, height: 0 });
    image.src = tileUrl(url);
  });
}

/** Puts one of the brand's pictures on the page: a vector as layers, anything else as a picture. */
export async function placeKitPicture(picture: KitPicture, actions: LibraryActions, share: number): Promise<void> {
  if (picture.vector) {
    try {
      actions.placeDrawing(await svgText(picture.url), { name: picture.name, share });
    } catch {
      actions.notice('That could not be placed.');
    }
    return;
  }
  // What the brand says it is, else what the picture turns out to be.
  const size = picture.width && picture.height ? { width: picture.width, height: picture.height } : await sizeOf(picture.url);
  actions.addPicture({
    assetId: picture.mediaAssetId ?? `brand-${hash(picture.url)}`,
    ...(picture.mediaAssetId ? { mediaAssetId: picture.mediaAssetId } : {}),
    name: picture.name,
    src: picture.url,
    ...size,
  });
}

/** A short, stable name for a picture that has no asset of its own. */
function hash(text: string): string {
  let h = 5381;
  for (let i = 0; i < text.length; i += 1) h = ((h << 5) + h + text.charCodeAt(i)) >>> 0;
  return h.toString(36);
}

// ── which brand ─────────────────────────────────────────────────────────────

/** The brands this file could be made in, read once for both the panel and its picker. */
function useBrandChoice() {
  const { context, choose } = useWork();
  const [all, setAll] = useState<BrandCard[] | null>(null);
  const [problem, setProblem] = useState(false);
  const [attempt, setAttempt] = useState(0);
  useEffect(() => {
    let live = true;
    setProblem(false);
    usableBrands()
      .then(brands => live && setAll(brands))
      .catch(() => live && setProblem(true));
    return () => { live = false; };
  }, [attempt]);
  return useMemo(() => context ? {
    ...brandChoices(context, all),
    loading: all === null && !problem,
    problem,
    retry: () => setAttempt(was => was + 1),
    pick: (value: string) => choose(value === 'none' ? null : value === 'project' ? 'project' : value),
  } : null, [context, all, problem, choose]);
}

/** A logo in a small, stable frame, with initials when a brand has no readable logo. */
function BrandAvatar({ brand, preview }: { brand: BrandCard | null; preview?: KitPicture | null }) {
  const logo = preview ?? (brand?.logoUrl ? { url: brand.logoUrl, vector: /\.svg(?:[?#]|$)/i.test(brand.logoUrl) } : null);
  const fitted = useFittedLogo(logo);
  const [failed, setFailed] = useState(false);
  useEffect(() => setFailed(false), [logo?.url]);
  const initials = brand?.name.split(/\s+/).slice(0, 2).map(word => word[0]).join('').toUpperCase();
  return <span aria-hidden className="brand-avatar" style={logo && !failed ? { background: fitted.ground ?? preview?.ground ?? PAPER } : undefined}>
    {logo && !failed ? <LogoArt fit={{ ...fitted, src: logo.vector ? logo.url : tileUrl(logo.url) }} className="brand-avatar-art" onError={() => setFailed(true)} /> : initials || <Icon name="brands" size={ICON_SIZE.control} />}
  </span>;
}

/** A compact chooser with searchable names, visible logos, and the current choice. */
function BrandPicker({ choice, kit }: { choice: NonNullable<ReturnType<typeof useBrandChoice>>; kit: BrandKit | null }) {
  const { context } = useWork();
  const [anchor, setAnchor] = useAnchor<HTMLButtonElement>();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [busy, setBusy] = useState(false);
  const search = useRef<HTMLInputElement>(null);
  const list = useRef<HTMLDivElement>(null);
  const brand = context?.brand ?? null;
  const why = !brand ? 'Choose a brand' : context?.brandChosen ? 'Selected for this file' : context?.project ? `From ${context.project.name}` : 'Selected for this file';
  const words = query.trim().toLowerCase();
  const visible = choice.options.filter(option => `${option.name} ${option.brand?.name ?? ''} ${option.note ?? ''}`.toLowerCase().includes(words));
  useEffect(() => {
    if (open) { setQuery(''); search.current?.focus(); }
  }, [open]);
  const close = () => { setOpen(false); anchor?.focus({ preventScroll: true }); };
  const pick = async (id: string) => {
    close();
    if (id === choice.now) return;
    setBusy(true);
    try { await choice.pick(id); } finally { setBusy(false); }
  };
  return <div className="brand-switcher">
    <p className="brand-switcher-label">Brand for this file</p>
    <button ref={setAnchor} type="button" className="brand-switcher-button" aria-label={`Switch brand: ${brand?.name ?? 'No brand'}`} aria-haspopup="dialog" aria-expanded={open} disabled={busy || !context?.canEdit} onClick={() => setOpen(was => !was)} onKeyDown={event => {
      if (event.key === 'ArrowDown' || event.key === 'ArrowUp') { event.preventDefault(); setOpen(true); }
    }}>
      <BrandAvatar brand={brand} preview={kit ? railMark(kit) : null} />
      <span className="brand-switcher-words"><strong>{brand?.name ?? 'No brand selected'}</strong><span>{busy ? 'Switching brand…' : !context?.canEdit ? 'View only' : why}</span></span>
      {busy ? <Spinner size={ICON_SIZE.small} /> : <Icon name="chevron" size={ICON_SIZE.small} />}
    </button>
    <Popover open={open} anchor={anchor} onClose={close} label="Choose brand" ownsKeyboard width={300} className="brand-picker">
      <label className="brand-picker-search field-focus"><Icon name="search" size={ICON_SIZE.control} /><input ref={search} value={query} onChange={event => setQuery(event.target.value)} aria-label="Search brands" placeholder="Search brands" onKeyDown={event => {
        if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
          event.preventDefault();
          const options = list.current?.querySelectorAll<HTMLButtonElement>('[role=option]');
          const next = event.key === 'ArrowDown' ? options?.[0] : options?.[options.length - 1];
          next?.focus();
        } else if (event.key === 'Enter' && visible.length === 1) { event.preventDefault(); void pick(visible[0]!.id); }
      }} /></label>
      <div ref={list} role="listbox" aria-label="Available brands" className="brand-picker-list ui-scroll" onKeyDown={event => {
        event.stopPropagation();
        if (!/^(ArrowDown|ArrowUp|Home|End)$/.test(event.key)) return;
        event.preventDefault();
        const options = [...event.currentTarget.querySelectorAll<HTMLButtonElement>('[role=option]')];
        const at = options.indexOf(document.activeElement as HTMLButtonElement);
        const next = event.key === 'Home' ? 0 : event.key === 'End' ? options.length - 1 : (at + (event.key === 'ArrowDown' ? 1 : -1) + options.length) % options.length;
        options[next]?.focus();
      }}>
        {visible.map(option => <button key={option.id} type="button" role="option" aria-selected={option.id === choice.now} onClick={() => void pick(option.id)} className="brand-picker-option">
          <BrandAvatar brand={option.brand} />
          <span className="brand-picker-words"><span>{option.name}</span>{option.note && <small>{option.note}</small>}</span>
          <span className="brand-picker-check">{option.id === choice.now && <Icon name="check" size={ICON_SIZE.small} />}</span>
        </button>)}
        {visible.length === 0 && !choice.loading && <p className="brand-picker-message" role="status">No brands found for “{query.trim()}”.</p>}
        {choice.loading && <p className="brand-picker-message" role="status">Loading brands…</p>}
        {choice.problem && <p className="brand-picker-message" role="status">Brands couldn’t be loaded. <button type="button" onClick={choice.retry}>Try again</button></p>}
      </div>
    </Popover>
  </div>;
}

// ── the panel ───────────────────────────────────────────────────────────────

export function BrandPanel({ actions, onClose }: { actions: LibraryActions; onClose: () => void }) {
  const { context, kit } = useWork();
  const choice = useBrandChoice();
  useKitFonts(kit);
  const [kind, setKind] = useState<BrandKind | null>(null);
  const brandId = context?.brand?.id ?? null;
  // Another brand opens on its own essentials, not inside a kind of the last one.
  useEffect(() => setKind(null), [brandId]);

  if (!context || !choice) return <>
    <PanelHeader title="Brand" onClose={onClose} />
    <Body label="Brand assets"><div role="status" aria-label="Loading brand"><div aria-hidden className="mb-[14px] h-[56px] rounded-[10px] bg-fill-2" /></div></Body>
  </>;

  const ready = kit && kit.id === brandId ? kit : null;
  if (ready && kind && brandRows(ready).some(row => row.kind === kind)) return <Inside kit={ready} kind={kind} actions={actions} onBack={() => setKind(null)} onClose={onClose} />;
  return <>
    <PanelHeader title="Brand" onClose={onClose} />
    <BrandPicker choice={choice} kit={ready} />
    <Body label="Brand assets">{!context.brand ? <Quiet>{context.project ? `Choose a brand for this file, or link one to ${context.project.name} in Flobi Projects.` : 'Choose a brand to use its logos, colours and type in this file.'}</Quiet> : ready ? <Home kit={ready} actions={actions} onOpen={setKind} /> : <div role="status" aria-label="Loading brand assets"><div aria-hidden className="grid grid-cols-2 gap-[8px]">{[0, 1].map(index => <span key={index} className="h-[80px] rounded-[9px] bg-fill-2" />)}</div></div>}</Body>
  </>;
}

/**
 * A logo's drawing, as large as its box allows. Once the drawing has been found in its
 * square, that part of a sharp rendition is shown — an SVG viewBox does the cutting and
 * the fitting; until then, and for a vector, the picture as it is.
 */
function LogoArt({ fit, className, onError }: { fit: ReturnType<typeof useFittedLogo>; className: string; onError?: () => void }) {
  if (!fit.src) return null;
  if (fit.box && fit.size) {
    return (
      <svg aria-hidden className={className} viewBox={`${fit.box.x} ${fit.box.y} ${fit.box.width} ${fit.box.height}`} preserveAspectRatio="xMidYMid meet">
        <image onError={onError} href={fit.src} width={fit.size.width} height={fit.size.height} preserveAspectRatio="none" />
      </svg>
    );
  }
  return <img onError={onError} src={fit.src} alt="" draggable={false} className={`${className} object-contain`} />;
}

/** The brand's essential assets, with the full collections one click away. */
function Home({ kit, actions, onOpen }: { kit: BrandKit; actions: LibraryActions; onOpen: (kind: BrandKind) => void }) {
  const { logo, others, colours, heading, body } = essentials(kit);
  const logos = logo ? [logo, ...others] : others;
  const rows = brandRows(kit);
  if (isBare(kit)) return <Quiet>{kit.name} has no logos, colours or type yet. They are made in Flobi Brands.</Quiet>;
  // Said once, for a brand that is only a logo so far: why there is nothing more here.
  const thin = kit.colours.length + kit.gradients.length + kit.styles.length === 0;
  return (
    <>
      {logos.length > 0 ? <section className="mb-[16px]">
        <Label>Logos</Label>
        <div className={`grid gap-[8px] ${logos.length > 1 ? 'grid-cols-2' : 'grid-cols-1'}`}>
          {logos.map(logo => <LogoTile key={logo.url} logo={logo} actions={actions} className="h-[80px]" />)}
        </div>
      </section> : null}
      {colours.length > 0 ? (
        <section className="mb-[16px]"><Label>Colours</Label><div className="flex gap-[8px]" role="group" aria-label="Brand colours">
          {colours.map((swatch) => (
            <button
              key={`${swatch.name}-${swatch.hex}`}
              type="button"
              aria-label={`${swatch.name}, ${swatch.hex}`}
              {...tip(`${swatch.name} · ${swatch.hex.toUpperCase()}`)}
              onClick={() => actions.useColour(swatch.hex)}
              className="h-[38px] min-w-0 flex-1 rounded-[10px] shadow-[inset_0_0_0_1px_rgb(0_0_0/0.18),0_0_0_1px_var(--separator)] transition-shadow hover:shadow-[inset_0_0_0_1px_rgb(0_0_0/0.18),0_0_0_2px_var(--accent)]"
              style={{ background: swatch.hex }}
            />
          ))}
        </div></section>
      ) : null}
      {heading ? (
        <section className="mb-[16px]"><Label>Typography</Label><div className={`grid gap-[8px] ${body ? 'grid-cols-2' : 'grid-cols-1'}`}>
          <TextTile kit={kit} actions={actions} style={heading} text="Heading" most={21} />
          {body ? <TextTile kit={kit} actions={actions} style={body} text="Body text" most={46} /> : null}
        </div></section>
      ) : null}
      {rows.map((row, index) => (
        // A line between rows, and above the first only when something sits over it.
        <button key={row.kind} type="button" onClick={() => onOpen(row.kind)} className={`-mx-[6px] flex h-[42px] w-[calc(100%+12px)] items-center gap-[10px] px-[6px] text-left transition-colors hover:bg-fill-2 ${index > 0 || others.length + colours.length > 0 || heading ? 'border-t border-separator' : ''}`}>
          <span className="min-w-0 flex-1 truncate text-[13px] font-medium text-label">{row.label}</span>
          <span className="tnum shrink-0 text-[12px] text-label-3">{row.count}</span>
          <span className="shrink-0 text-label-3">
            <Icon name="chevron-right" size={12} />
          </span>
        </button>
      ))}
      {thin ? <p className="px-[4px] pt-[10px] text-[12px] leading-[17px] text-label-2">{kit.name} has no colours or type yet. They are added in Flobi Brands.</p> : null}
    </>
  );
}

/** A line of the brand's type to add, drawn in its own face. */
function TextTile({ kit, actions, style, text, most }: { kit: BrandKit; actions: LibraryActions; style: BrandKit['styles'][number]; text: string; most: number }) {
  return (
    <button type="button" {...tip(`${style.role} · ${style.family}`)} onClick={() => addKitText(actions, kit, style, text)} {...carry((at) => addKitText(aimed(actions, at), kit, style, text))} className="flex h-[50px] min-w-0 items-center rounded-[10px] bg-fill-2 px-[12px] text-left text-label transition-colors hover:bg-fill-selected">
      <span className="min-w-0 truncate" style={kitTextLook(kit, style, most)}>{text}</span>
    </button>
  );
}

/** One kind of the brand in full, and the way back to the brand. */
function Inside({ kit, kind, actions, onBack, onClose }: { kit: BrandKit; kind: BrandKind; actions: LibraryActions; onBack: () => void; onClose: () => void }) {
  const { graphics } = kit;
  const title = brandRows(kit).find((row) => row.kind === kind)?.label.replace(/^All /, '') ?? '';
  let body: ReactNode = null;
  if (kind === 'logos') {
    body = logoGroups(graphics.logos).map((group) => (
      <section key={group.title} className={GROUP}>
        <Label>{group.title}</Label>
        <div className="grid grid-cols-2 gap-[8px]">
          {group.logos.map((logo) => (
            <LogoTile key={logo.url} logo={logo} actions={actions} className="aspect-[4/3]" />
          ))}
        </div>
      </section>
    ));
  } else if (kind === 'colours') {
    body = <Colours kit={kit} actions={actions} />;
  } else if (kind === 'type') {
    body = <KitStyles kit={kit} actions={actions} meta />;
  } else if (kind === 'icons') {
    body = <KitIcons icons={graphics.icons} actions={actions} />;
  } else if (kind === 'graphics') {
    body = <KitWall pictures={[...graphics.supergraphics, ...graphics.patterns, ...graphics.characters]} actions={actions} />;
  } else {
    body = <KitWall pictures={graphics.imagery} actions={actions} />;
  }
  return (
    <>
      <PanelHeader title={title} back={onBack} backLabel={kit.name} onClose={onClose} />
      <Body label={`${kit.name} ${title.toLowerCase()}`}>{body}</Body>
    </>
  );
}

/** One named run inside a kind: room above every one but the first. */
const GROUP = 'mt-[14px] first:mt-0';

function Label({ children }: { children: ReactNode }) {
  return <p className="mb-[6px] text-[11px] font-medium text-label-3">{children}</p>;
}

/** Every colour the brand has, by its group and by its name: swatches, ramps, gradients. */
function Colours({ kit, actions }: { kit: BrandKit; actions: LibraryActions }) {
  return (
    <>
      {kit.colours.map((group) =>
        group.ramp ? (
          <section key={group.name} className={GROUP}>
            <Label>{group.name}</Label>
            <div role="group" aria-label={group.name} className="flex h-[24px] overflow-hidden rounded-[7px] shadow-[0_0_0_1px_var(--separator)]">
              {group.swatches.map((swatch) => (
                <button key={swatch.name} type="button" aria-label={`${swatch.name}, ${swatch.hex}`} {...tip(`${swatch.name} · ${swatch.hex.toUpperCase()}`)} onClick={() => actions.useColour(swatch.hex)} className="h-full flex-1 transition-[flex-grow] hover:grow-[1.6]" style={{ background: swatch.hex }} />
              ))}
            </div>
          </section>
        ) : (
          <section key={group.name} className={GROUP}>
            <Label>{group.name}</Label>
            {group.swatches.map((swatch) => (
              <button key={`${swatch.name}-${swatch.hex}`} type="button" onClick={() => actions.useColour(swatch.hex)} className="-mx-[6px] flex h-[34px] w-[calc(100%+12px)] items-center gap-[10px] rounded-[9px] px-[6px] text-left transition-colors hover:bg-fill-2">
                <span aria-hidden className="size-[22px] shrink-0 rounded-full shadow-[inset_0_0_0_1px_rgb(0_0_0/0.18),0_0_0_1px_var(--separator)]" style={{ background: swatch.hex }} />
                <span className="min-w-0 flex-1 truncate text-[13px] text-label">{swatch.name}</span>
                <span className="tnum shrink-0 text-[12px] text-label-3">{swatch.hex.toUpperCase()}</span>
              </button>
            ))}
          </section>
        ),
      )}
      {kit.gradients.length > 0 ? (
        <section className={GROUP}>
          <Label>Gradients</Label>
          <div className="grid grid-cols-2 gap-[8px]">
            {kit.gradients.map((gradient) => (
              <button
                key={gradient.name}
                type="button"
                aria-label={gradient.name}
                {...tip(gradient.name)}
                onClick={() => actions.useColour(gradient)}
                className="h-[44px] rounded-[9px] shadow-[0_0_0_1px_var(--separator)] transition-shadow hover:shadow-[0_0_0_2px_var(--accent)]"
                style={{ background: `linear-gradient(${gradient.angle}deg, ${gradient.from}, ${gradient.to})` }}
              />
            ))}
          </div>
        </section>
      ) : null}
    </>
  );
}

// ── tiles ───────────────────────────────────────────────────────────────────

/** A logo to place, on the ground the brand shows it on, at the size its drawing allows. */
function LogoTile({ logo, actions, className }: { logo: KitPicture; actions: LibraryActions; className: string }) {
  const fit = useFittedLogo(logo);
  return (
    <button
      type="button"
      aria-label={logo.name}
      {...tip(logo.name)}
      onClick={() => void placeKitPicture(logo, actions, LOGO_SHARE)}
      {...carry((at) => void placeKitPicture(logo, aimed(actions, at), LOGO_SHARE))}
      className={`relative w-full overflow-hidden rounded-[9px] shadow-[0_0_0_1px_var(--separator)] transition-shadow hover:shadow-[0_0_0_2px_var(--accent)] ${className}`}
      // On the colour it was drawn on, else the ground the brand shows it on, else paper: a white logo is nothing on a light tile.
      style={{ background: fit.ground ?? logo.ground ?? PAPER }}
    >
      <LogoArt fit={fit} className="absolute left-[12%] top-[14%] h-[72%] w-[76%]" />
    </button>
  );
}

/** The brand's mark, small: what the rail wears in place of the tag. */
export function BrandMark({ logo, size }: { logo: KitPicture; size: number }) {
  const fit = useFittedLogo(logo);
  return (
    <span aria-hidden className="relative -my-px block shrink-0 overflow-hidden rounded-[7px] shadow-[0_0_0_1px_var(--separator)]" style={{ width: size, height: size, background: fit.ground ?? logo.ground ?? PAPER }}>
      <LogoArt fit={fit} className="absolute left-[11%] top-[11%] size-[78%]" />
    </span>
  );
}

/** A brand's icons, four abreast. A vector one is drawn in the panel's text colour; a picture as it is. */
export function KitIcons({ icons, actions }: { icons: readonly KitPicture[]; actions: LibraryActions }) {
  return (
    <div className="grid grid-cols-4 gap-[8px]">
      {icons.map((icon) => (
        <button
          key={icon.url}
          type="button"
          aria-label={icon.name}
          {...tip(icon.name)}
          onClick={() => void placeKitPicture(icon, actions, ICON_SHARE)}
          {...carry((at) => void placeKitPicture(icon, aimed(actions, at), ICON_SHARE))}
          className="grid aspect-square place-items-center overflow-hidden rounded-[9px] bg-[#f2eee3] p-[8px] shadow-[0_0_0_1px_var(--separator)] transition-shadow hover:shadow-[0_0_0_2px_var(--accent)]"
        >
          <img src={icon.vector ? icon.url : tileUrl(icon.url)} alt="" loading="lazy" draggable={false} className="max-h-full max-w-full object-contain" />
        </button>
      ))}
    </div>
  );
}

/** A brand's pictures two abreast. Their shapes are read off the tiles: a brand does not always say. */
export function KitWall({ pictures, actions }: { pictures: readonly KitPicture[]; actions: LibraryActions }) {
  return (
    <Wall
      items={pictures}
      ratio={(picture) => (picture.width && picture.height ? picture.width / picture.height : 1)}
      tile={(picture) => (
        <button
          key={picture.url}
          type="button"
          aria-label={picture.name}
          {...tip(picture.name)}
          onClick={() => void placeKitPicture(picture, actions, LOGO_SHARE)}
          {...carry((at) => void placeKitPicture(picture, aimed(actions, at), LOGO_SHARE))}
          className="block w-full overflow-hidden rounded-[9px] bg-fill-2 shadow-[0_0_0_1px_var(--separator)] transition-shadow hover:shadow-[0_0_0_2px_var(--accent)]"
        >
          <img src={picture.vector ? picture.url : tileUrl(picture.url)} alt="" loading="lazy" draggable={false} className="block w-full" />
        </button>
      )}
    />
  );
}
